/**
 * Worker-Onboarding-Schemas.
 *
 * Alle Felder optional → Auto-Save erlaubt unvollständige Drafts.
 * Pflichtfeld-Check passiert serverseitig im `submit`, nicht im Auto-Save.
 *
 * Felder-Auswahl folgt Spec v1.1 §5.1.
 */
import { z } from 'zod';
import { INSURANCE_STATUSES, MARITAL_STATUSES } from '../enums/index.js';

// ─── Step 1: Persönlich ──────────────────────────────────────────────────────

export const workerStep1Schema = z.object({
  firstName: z.string().trim().min(1).max(100).optional(),
  lastName: z.string().trim().min(1).max(100).optional(),
  maidenName: z.string().trim().max(100).optional().nullable(),
  dateOfBirth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format YYYY-MM-DD')
    .optional(),
  placeOfBirth: z.string().trim().max(100).optional(),
  addressStreet: z.string().trim().max(200).optional(),
  addressZip: z.string().trim().max(10).optional(),
  addressCity: z.string().trim().max(100).optional(),
  addressCountry: z.string().trim().length(2, 'Ländercode ISO-3166 (z. B. DE)').optional(),
  phone: z.string().trim().max(30).optional(),
  nationality: z.string().trim().max(100).optional(),
});

// ─── Step 2: Steuer & Bank ───────────────────────────────────────────────────

export const workerStep2Schema = z.object({
  taxId: z
    .string()
    .trim()
    .regex(/^\d{11}$/, 'Steueridentifikationsnummer hat genau 11 Ziffern')
    .optional()
    .or(z.literal('')),
  taxClass: z.string().trim().max(10).optional(),
  religiousAffiliation: z.string().trim().max(50).optional(),
  childAllowances: z.number().min(0).max(20).optional(),
  maritalStatus: z.enum(MARITAL_STATUSES).optional(),
  numberOfChildren: z.number().int().min(0).max(20).optional(),
  bankAccountHolder: z.string().trim().max(200).optional(),
  iban: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/i, 'IBAN-Format ungültig (z. B. DE89370400440532013000)')
    .optional()
    .or(z.literal('')),
  bic: z.string().trim().max(11).optional(),
});

// ─── Step 3: SV & Aufenthalt ─────────────────────────────────────────────────

export const workerStep3Schema = z.object({
  socialSecurityNumber: z.string().trim().max(20).optional(),
  healthInsuranceProvider: z.string().trim().max(100).optional(),
  insuranceStatus: z.enum(INSURANCE_STATUSES).optional(),
  residencePermitType: z.string().trim().max(100).optional(),
  residencePermitExpiry: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format YYYY-MM-DD')
    .optional()
    .or(z.literal('')),
  workAuthorizationConfirmed: z.boolean().optional(),
  workRestrictions: z.string().trim().max(500).optional(),
});

// ─── Combined Upsert (Auto-Save) ─────────────────────────────────────────────

export const upsertWorkerSchema = workerStep1Schema
  .merge(workerStep2Schema)
  .merge(workerStep3Schema);

export type UpsertWorkerInput = z.infer<typeof upsertWorkerSchema>;

// ─── Staff-only profile fields ──────────────────────────────────────────────

/**
 * Felder, die nicht Teil des Worker-Self-Service-Onboardings sind, aber zur
 * vollständigen Staff-Stammdatenpflege gehören. System-Links, Status und
 * Review-Metadaten bleiben bewusst außerhalb dieses Contracts.
 */
export const staffWorkerFieldsSchema = z.object({
  hourlyWage: z
    .string()
    .trim()
    .regex(/^\d{1,4}(?:\.\d{1,2})?$/, 'Stundenlohn mit höchstens 2 Nachkommastellen')
    .optional(),
  wageCurrency: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/, 'Währung als ISO-4217-Code, z. B. EUR')
    .optional(),
  severeDisability: z.boolean().optional(),
  disabilityDegree: z.number().int().min(0).max(100).optional(),
  disabilityValidUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format YYYY-MM-DD')
    .optional(),
  highestEducation: z.string().trim().max(200).optional(),
  drivingLicenseClass: z.string().trim().max(100).optional(),
  professionalCertifications: z.array(z.string().trim().min(1).max(200)).max(100).optional(),
  notes: z.string().trim().max(5000).optional(),
});
export type StaffWorkerFieldsInput = z.infer<typeof staffWorkerFieldsSchema>;

export const staffUpdateWorkerSchema = upsertWorkerSchema.merge(staffWorkerFieldsSchema);
export type StaffUpdateWorkerInput = z.infer<typeof staffUpdateWorkerSchema>;

/** Nullable DB-Felder, die Staff durch Leeren des Formularfelds entfernen darf. */
export const STAFF_CLEARABLE_WORKER_FIELDS = [
  'firstName',
  'lastName',
  'maidenName',
  'dateOfBirth',
  'placeOfBirth',
  'addressStreet',
  'addressZip',
  'addressCity',
  'addressCountry',
  'phone',
  'nationality',
  'taxId',
  'taxClass',
  'religiousAffiliation',
  'childAllowances',
  'maritalStatus',
  'numberOfChildren',
  'bankAccountHolder',
  'iban',
  'bic',
  'hourlyWage',
  'socialSecurityNumber',
  'healthInsuranceProvider',
  'insuranceStatus',
  'residencePermitType',
  'residencePermitExpiry',
  'workAuthorizationConfirmed',
  'workRestrictions',
  'severeDisability',
  'disabilityDegree',
  'disabilityValidUntil',
  'highestEducation',
  'drivingLicenseClass',
  'professionalCertifications',
  'notes',
] as const;

/** Pflichtfelder beim finalen Submit. Server-seitige Prüfung. */
export const submitWorkerRequiredFields = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'addressStreet',
  'addressZip',
  'addressCity',
  'phone',
  'nationality',
  'taxId',
  'iban',
  'socialSecurityNumber',
  'healthInsuranceProvider',
  'insuranceStatus',
] as const;
